// I riquadri Cliente / Impianto / Gestione del cruscotto d'impianto (0050).
import { z } from 'zod'
import { supabase } from '../lib/supabase'
import type { Indirizzo } from '../anagrafiche/CampoIndirizzo'

export const RUOLI_REFERENTE = ['impianto', 'letture', 'altro'] as const
export type RuoloReferente = (typeof RUOLI_REFERENTE)[number]
export const ETICHETTA_RUOLO: Record<RuoloReferente, string> = {
  impianto: 'Ref. impianto',
  letture: 'Ref. letture',
  altro: 'Altro ref.',
}
export const TENSIONI = ['BT', 'MT', 'AT'] as const

export interface Referente {
  readonly id: number
  readonly ruolo: RuoloReferente
  readonly nome: string
  readonly email: string | null
  readonly email2: string | null
  readonly telefono: string | null
}

export interface Scheda {
  readonly aziendaId: number
  readonly ragioneSociale: string
  readonly partitaIva: string | null
  readonly codiceFiscale: string
  readonly sedeLegale: Indirizzo
  readonly clienteDal: string | null
  readonly codiceDitta: string
  readonly potenza: string | null
  readonly tensione: (typeof TENSIONI)[number] | null
  readonly censimp: string | null
  readonly dataAttivazione: string | null
  readonly contrattiGse: readonly string[]
  readonly ubicazione: Indirizzo
  /** Ufficio delle Dogane competente per la licenza. */
  readonly ufficio: string
  readonly pod: readonly string[]
  readonly referenti: readonly Referente[]
}

const VUOTO: Indirizzo = { comune: '', provincia: '', indirizzo: '' }
const indirizzo = (v: unknown): Indirizzo => ({ ...VUOTO, ...((v ?? {}) as Partial<Indirizzo>) })
type Annidato<T> = T | T[] | null
const uno = <T,>(v: Annidato<T>): T | null => (Array.isArray(v) ? (v[0] ?? null) : v)

interface Riga {
  codice_ditta: string
  potenza_nominale: number | string | null
  tensione: Scheda['tensione']
  censimp: string | null
  data_attivazione: string | null
  contratti_gse: string[] | null
  ubicazione: unknown
  ufficio_contabilita: unknown
  azienda: Annidato<{
    id: number
    ragione_sociale: string
    partita_iva: string | null
    codice_fiscale: string
    sede_legale: unknown
    cliente_dal: string | null
  }>
  contatore: { pod: { codice_pod: string }[] }[] | null
  referente: { id: number; ruolo: RuoloReferente; nome: string; email: string | null; email_2: string | null; telefono: string | null }[] | null
}

export async function caricaScheda(impiantoId: number): Promise<Scheda> {
  const { data, error } = await supabase
    .from('impianto')
    .select(
      'codice_ditta, potenza_nominale, tensione, censimp, data_attivazione, contratti_gse, ubicazione, ufficio_contabilita, azienda(id, ragione_sociale, partita_iva, codice_fiscale, sede_legale, cliente_dal), contatore(pod(codice_pod)), referente(id, ruolo, nome, email, email_2, telefono)',
    )
    .eq('id', impiantoId)
    .single()
  if (error) throw new Error(error.message)
  const r = data as unknown as Riga
  const a = uno(r.azienda)
  return {
    aziendaId: a?.id ?? 0,
    ragioneSociale: a?.ragione_sociale ?? '',
    partitaIva: a?.partita_iva ?? null,
    codiceFiscale: a?.codice_fiscale ?? '',
    sedeLegale: indirizzo(a?.sede_legale),
    clienteDal: a?.cliente_dal ?? null,
    codiceDitta: r.codice_ditta,
    potenza: r.potenza_nominale === null ? null : String(r.potenza_nominale),
    tensione: r.tensione,
    censimp: r.censimp,
    dataAttivazione: r.data_attivazione,
    contrattiGse: r.contratti_gse ?? [],
    ubicazione: indirizzo(r.ubicazione),
    ufficio: indirizzo(r.ufficio_contabilita).comune,
    pod: [...new Set((r.contatore ?? []).flatMap((c) => c.pod.map((p) => p.codice_pod)))],
    referenti: (r.referente ?? [])
      .map((x) => ({ id: x.id, ruolo: x.ruolo, nome: x.nome, email: x.email, email2: x.email_2, telefono: x.telefono }))
      .sort((x, y) => RUOLI_REFERENTE.indexOf(x.ruolo) - RUOLI_REFERENTE.indexOf(y.ruolo) || x.id - y.id),
  }
}

const facoltativo = z
  .string()
  .trim()
  .transform((v) => (v === '' ? null : v))
const email = z
  .string()
  .trim()
  .refine((v) => v === '' || z.email().safeParse(v).success, 'Email non valida')
  .transform((v) => (v === '' ? null : v))
const data = z
  .string()
  .refine((v) => v === '' || /^\d{4}-\d{2}-\d{2}$/.test(v), 'Data non valida')
  .transform((v) => (v === '' ? null : v))

export const SchemaDatiImpianto = z.object({
  censimp: facoltativo,
  dataAttivazione: data,
  tensione: z.enum(TENSIONI).nullable(),
  // Si scrivono separati da virgola o a capo, come nella colonna dell'Excel.
  contrattiGse: z.string().transform((v) => v.split(/[,\n]/).map((c) => c.trim()).filter(Boolean)),
})
export type DatiImpiantoScheda = z.input<typeof SchemaDatiImpianto>

export async function salvaDatiImpianto(impiantoId: number, dati: DatiImpiantoScheda): Promise<void> {
  const d = SchemaDatiImpianto.parse(dati)
  const { error } = await supabase
    .from('impianto')
    .update({ censimp: d.censimp, data_attivazione: d.dataAttivazione, tensione: d.tensione, contratti_gse: d.contrattiGse })
    .eq('id', impiantoId)
  if (error) throw new Error(error.message)
}

export const SchemaDatiCliente = z.object({
  sedeLegale: z.object({ comune: z.string().trim(), provincia: z.string().trim(), indirizzo: z.string().trim() }),
  clienteDal: data,
})
export type DatiCliente = z.input<typeof SchemaDatiCliente>

export async function salvaDatiCliente(aziendaId: number, dati: DatiCliente): Promise<void> {
  const d = SchemaDatiCliente.parse(dati)
  const { error } = await supabase
    .from('azienda')
    .update({ sede_legale: d.sedeLegale, cliente_dal: d.clienteDal })
    .eq('id', aziendaId)
  if (error) throw new Error(error.message)
}

export const SchemaReferente = z.object({
  ruolo: z.enum(RUOLI_REFERENTE),
  nome: z.string().trim().min(1, 'Il nome e obbligatorio'),
  email,
  email2: email,
  telefono: facoltativo,
})
export type DatiReferente = z.input<typeof SchemaReferente>

/** `id` nullo = nuovo referente. */
export async function salvaReferente(id: number | null, impiantoId: number, dati: DatiReferente): Promise<void> {
  const d = SchemaReferente.parse(dati)
  const riga = { ruolo: d.ruolo, nome: d.nome, email: d.email, email_2: d.email2, telefono: d.telefono }
  const { error } =
    id === null
      ? await supabase.from('referente').insert({ ...riga, impianto_id: impiantoId })
      : await supabase.from('referente').update(riga).eq('id', id)
  if (error) throw new Error(error.message)
}

export async function eliminaReferente(id: number): Promise<void> {
  const { error } = await supabase.from('referente').delete().eq('id', id)
  if (error) throw new Error(error.message)
}
